import React, { useState } from "react";
import {
  Table,
  Button,
  Tag,
  Select,
  Input,
  Space,
  Collapse,
  Pagination,
  Empty,
  message,
  Modal,
  Checkbox,
  Spin,
} from "antd";
import {
  PhoneOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  ShopOutlined,
  LoginOutlined,
  LoadingOutlined,
  UserOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ReactComponent as WhatsAppIcon } from "../../../assets/images/icons/whatsappGreenEmoji.svg";
import NumberFormat from "react-number-format";
import BusinessDetailsModal from "./BusinessDetailsModal";
import { setImpersonationSession } from "../../../slices/impersonationSlice";
import {
  _create_impersonation_session,
  _get_support_staffs,
  _assign_business_to_officer,
  _get_admin_zones,
} from "../../../common/axios_services";

const { Option } = Select;
const { Search } = Input;

const BusinessList = ({
  businesses,
  loading,
  onViewBusiness,
  onRefresh,
  activeTab,
  userRole,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [filteredBusinesses, setFilteredBusinesses] = useState(businesses);
  const [serviceFilter, setServiceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [accessingBusinessId, setAccessingBusinessId] = useState(null);

  const [seFilter, setSeFilter] = useState(null);
  const [smFilter, setSmFilter] = useState(null);
  const [soFilter, setSoFilter] = useState(null);
  const [businessStatusFilter, setBusinessStatusFilter] = useState(null);
  const [zoneFilter, setZoneFilter] = useState(null);
  const [availableZones, setAvailableZones] = useState([]);
  const [availableSEs, setAvailableSEs] = useState([]);
  const [availableSMs, setAvailableSMs] = useState([]);

  const [smSoModalVisible, setSmSoModalVisible] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [smSoModalLoading, setSmSoModalLoading] = useState(false);
  const [availableSOs, setAvailableSOs] = useState([]);
  const [selectedSOId, setSelectedSOId] = useState(null);
  const [changingBusinessId, setChangingBusinessId] = useState(null);
  const [changingBusinessName, setChangingBusinessName] = useState("");

  const services = ["Restaurant", "Wholesale", "Retail"];
  const statuses = ["OPEN", "CLOSED"];
  const businessStatuses = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const handleAccessBusiness = async (businessId, businessName) => {
    try {
      setAccessingBusinessId(businessId);
      const response = await _create_impersonation_session(businessId);

      const { sessionId, token, tokenPreview, expiresAt } = response.data?.data;

      dispatch(
        setImpersonationSession({
          sessionId,
          businessId,
          businessName,
          token,
          tokenPreview,
          expiresAt,
        }),
      );

      message.success(`Accessing ${businessName} dashboard...`);
      navigate("/Restaurant/profile");
    } catch (error) {
      console.error("Failed to access business:", error);
      const errorData = error.response?.data;

      if (errorData?.code === "STAFF_ACTIVE_SESSION_EXISTS" && errorData.existingSession) {
        const { id, businessId: bId, businessName: bName, tokenPreview, expiresAt } = errorData.existingSession;

        dispatch(
          setImpersonationSession({
            sessionId: id,
            businessId: bId,
            businessName: bName,
            token: null, // Full token not available on recovery, user may need to end and restart if they lost it
            tokenPreview,
            expiresAt,
          })
        );

        message.warning(`Redirecting to your existing active session for ${bName}...`);
        navigate("/Restaurant/profile");
        return;
      }

      message.error(
        errorData?.message ||
        "Failed to access business. Please try again.",
      );
    } finally {
      setAccessingBusinessId(null);
    }
  };

  React.useEffect(() => {
    fetchZones();
    fetchAllStaff();
  }, []);

  const fetchZones = async () => {
    try {
      const response = await _get_admin_zones({ page: 1, limit: 100 });
      const zones = response?.data?.data?.data || response?.data?.data || [];
      setAvailableZones(
        zones.map((zone) => ({
          label: zone.name,
          value: zone.id,
        })),
      );
    } catch (error) {
      console.error("Error fetching zones:", error);
    }
  };

  const fetchAllStaff = async () => {
    try {
      const response = await _get_support_staffs({ page: 1, limit: 100 });
      const staffData =
        response?.data?.data?.data || response?.data?.data || [];

      const supportExecutives = staffData.filter(
        (staff) =>
          staff.role === "SUPPORT_EXECUTIVE" ||
          staff.role === "support_executive" ||
          staff.roleName?.toLowerCase()?.includes("support executive"),
      );

      const supportManagers = staffData.filter(
        (staff) =>
          staff.role === "SUPPORT_MANAGER" ||
          staff.role === "support_manager" ||
          staff.roleName?.toLowerCase()?.includes("support manager"),
      );

      const supportOfficers = staffData.filter(
        (staff) =>
          staff.role === "SUPPORT_OFFICER" ||
          staff.role === "support_officer" ||
          staff.roleName?.toLowerCase()?.includes("support officer"),
      );

      setAvailableSEs(
        supportExecutives.map((se) => ({
          label:
            `${se.firstName || ""} ${se.lastName || ""}`.trim() ||
            se.email ||
            "Unknown",
          value: se.id,
        })),
      );

      setAvailableSMs(
        supportManagers.map((sm) => ({
          label:
            `${sm.firstName || ""} ${sm.lastName || ""}`.trim() ||
            sm.email ||
            "Unknown",
          value: sm.id,
        })),
      );

      setAvailableSOs(
        supportOfficers.map((so) => ({
          label:
            `${so.firstName || ""} ${so.lastName || ""}`.trim() ||
            so.email ||
            "Unknown",
          value: so.id,
        })),
      );
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  const fetchAvailableSOs = async () => {
    setSmSoModalLoading(true);
    try {
      const response = await _get_support_staffs({
        page: 1,
        limit: 100,
      });

      const staffData =
        response?.data?.data?.data || response?.data?.data || [];

      const supportOfficers = staffData.filter(
        (staff) =>
          staff.role === "SUPPORT_OFFICER" ||
          staff.role === "support_officer" ||
          staff.roleName?.toLowerCase()?.includes("support officer"),
      );

      setAvailableSOs(
        supportOfficers.map((so) => ({
          label:
            `${so.firstName || ""} ${so.lastName || ""}`.trim() ||
            so.email ||
            "Unknown",
          value: so.id,
          email: so.email,
          phone: so.phoneNumber,
        })),
      );
    } catch (error) {
      console.error("Error fetching support officers:", error);
      message.error("Failed to load support officers");
    } finally {
      setSmSoModalLoading(false);
    }
  };

  const openSOModal = (businessId, businessName, currentSOId) => {
    setChangingBusinessId(businessId);
    setChangingBusinessName(businessName);
    setSelectedSOId(currentSOId || null);
    fetchAvailableSOs();
    setSmSoModalVisible(true);
  };

  const handleConfirmSOChange = async () => {
    if (!selectedSOId) {
      message.warning("Please select a Support Officer");
      return;
    }

    setSmSoModalLoading(true);
    try {
      await _assign_business_to_officer(changingBusinessId, selectedSOId);

      message.success("Support Officer updated successfully");

      setSmSoModalVisible(false);
      setSelectedSOId(null);
      setChangingBusinessId(null);
      setChangingBusinessName("");

      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Error updating SO:", error);
      message.error(
        error.response?.data?.message ||
        "Failed to update Support Officer. Please try again.",
      );
    } finally {
      setSmSoModalLoading(false);
    }
  };

  React.useEffect(() => {
    let filtered = businesses;

    if (serviceFilter) {
      filtered = filtered.filter(
        (business) => business.serviceType === serviceFilter,
      );
    }

    if (statusFilter) {
      const isOpen = statusFilter === "OPEN";
      filtered = filtered.filter((business) => business.isOpen === isOpen);
    }

    if (seFilter) {
      filtered = filtered.filter(
        (business) => business.supportExecutive?.id === seFilter,
      );
    }

    if (smFilter) {
      filtered = filtered.filter(
        (business) => business.supportManager?.id === smFilter,
      );
    }

    if (soFilter) {
      filtered = filtered.filter(
        (business) => business.supportOfficer?.id === soFilter,
      );
    }

    if (businessStatusFilter) {
      filtered = filtered.filter(
        (business) => business.status === businessStatusFilter,
      );
    }

    if (zoneFilter) {
      filtered = filtered.filter(
        (business) =>
          business.zone?.id === zoneFilter || business.lga === zoneFilter,
      );
    }

    if (searchText) {
      filtered = filtered.filter(
        (business) =>
          business.name.toLowerCase().includes(searchText.toLowerCase()) ||
          business.address.toLowerCase().includes(searchText.toLowerCase()),
      );
    }

    setFilteredBusinesses(filtered);
  }, [
    businesses,
    serviceFilter,
    statusFilter,
    searchText,
    seFilter,
    smFilter,
    soFilter,
    businessStatusFilter,
    zoneFilter,
  ]);

  return (
    <div>
      <div
        style={{ marginBottom: 16, display: "flex", gap: 16, flexWrap: "wrap" }}
      >
        <Search
          placeholder="Search businesses..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ minWidth: 200, flex: 1 }}
        />

        <Button
          type="primary"
          icon={<FilterOutlined />}
          onClick={() => setFilterModalVisible(true)}
        >
          Filters
          {(seFilter ||
            smFilter ||
            soFilter ||
            businessStatusFilter ||
            zoneFilter ||
            serviceFilter ||
            statusFilter) && <span style={{ marginLeft: 8 }}>•</span>}
        </Button>
      </div>

      {/* Empty State */}
      {!loading && (!filteredBusinesses || filteredBusinesses.length === 0) && (
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              searchText ||
                serviceFilter ||
                statusFilter ||
                seFilter ||
                smFilter ||
                soFilter ||
                businessStatusFilter ||
                zoneFilter
                ? "No businesses match your filters"
                : "No businesses assigned to you yet"
            }
          >
            {(searchText ||
              serviceFilter ||
              statusFilter ||
              seFilter ||
              smFilter ||
              soFilter ||
              businessStatusFilter ||
              zoneFilter) && (
                <Button
                  onClick={() => {
                    setSearchText("");
                    setServiceFilter("");
                    setStatusFilter("");
                    setSeFilter(null);
                    setSmFilter(null);
                    setSoFilter(null);
                    setBusinessStatusFilter(null);
                    setZoneFilter(null);
                  }}
                >
                  Clear Filters
                </Button>
              )}
          </Empty>
        </div>
      )}

      {/* Mobile Collapse View */}
      <div className="mobile topup-data">
        <Collapse defaultActiveKey={[]}>
          {filteredBusinesses &&
            filteredBusinesses.length > 0 &&
            filteredBusinesses.map((business, index) => (
              <Collapse.Panel
                showArrow={false}
                key={index}
                header={
                  <p style={{ display: "inline" }}>
                    {business.name}&nbsp;-&nbsp;
                    <Tag color="blue">{business?.lga}</Tag>
                  </p>
                }
              >
                <div>
                  <ul className="mobile-list-flex">
                    <li className="flex gap-4">
                      <span>Business Name:</span>
                      <span className="text-right font-semibold">
                        {business.name}
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Status:</span>
                      <span>
                        <Tag
                          color={business.status === "active" ? "green" : "red"}
                        >
                          {business.status}
                        </Tag>
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Telephone:</span>
                      <span>
                        <Space>
                          <Button
                            type="link"
                            icon={<PhoneOutlined />}
                            href={`tel:+234${business.businessTelephone}`}
                            style={{ padding: 0 }}
                          >
                            {business.businessTelephone
                              ? "+234" + business.businessTelephone
                              : "N/A"}
                          </Button>
                          <Button
                            type="link"
                            icon={
                              <WhatsAppIcon style={{ width: 14, height: 14 }} />
                            }
                            href={`https://wa.me/234${business.businessTelephone?.replace(
                              /^\+/,
                              "",
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ padding: 0 }}
                            title="Chat on WhatsApp"
                          />
                        </Space>
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Email:</span>
                      <span>{business.businessEmail}</span>
                    </li>
                    <li className="flex gap-4">
                      <span>Location:</span>
                      <span>
                        <div style={{ maxWidth: 200 }} className="text-right">
                          <EnvironmentOutlined style={{ marginRight: 8 }} />
                          <span style={{ wordBreak: "break-word" }}>
                            {business.address}
                          </span>
                        </div>
                      </span>
                    </li>
                    {business.supportOfficer && (
                      <li className="flex gap-4">
                        <span>Support Officer:</span>
                        <span className="text-right">
                          {business.supportOfficer.name}
                          <Button
                            type="link"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openSOModal(
                                business.id,
                                business.name,
                                business.supportOfficer?.id,
                              );
                            }}
                            style={{ marginLeft: 8, padding: 0 }}
                          >
                            Change
                          </Button>
                        </span>
                      </li>
                    )}
                    {!business.supportOfficer && (
                      <li className="flex gap-4">
                        <span>Support Officer:</span>
                        <span className="text-right">
                          <Button
                            type="link"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openSOModal(business.id, business.name, null);
                            }}
                            style={{ padding: 0 }}
                          >
                            Assign
                          </Button>
                        </span>
                      </li>
                    )}
                    <li className="flex gap-4">
                      <span>Sales Till Date:</span>
                      <span>
                        <NumberFormat
                          thousandSeparator={true}
                          prefix="₦"
                          displayType="text"
                          value={business.totalSales || 0}
                        />
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Actions:</span>
                      <span>
                        <Space
                          wrap
                          direction="vertical"
                          style={{ width: "100%" }}
                        >
                          <Button
                            type="primary"
                            icon={<EyeOutlined />}
                            onClick={() => {
                              setSelectedBusiness(business);
                              setModalVisible(true);
                            }}
                          >
                            View Details
                          </Button>
                          <Button
                            type="primary"
                            danger
                            icon={<LoginOutlined />}
                            loading={accessingBusinessId === business.id}
                            onClick={() =>
                              handleAccessBusiness(business.id, business.name)
                            }
                          >
                            Access Dashboard
                          </Button>
                        </Space>
                      </span>
                    </li>
                  </ul>
                </div>
              </Collapse.Panel>
            ))}
        </Collapse>
        <div style={{ marginTop: "2%" }}></div>
      </div>

      <BusinessDetailsModal
        visible={modalVisible}
        business={selectedBusiness}
        activeTab={activeTab}
        onCancel={() => {
          setModalVisible(false);
          setSelectedBusiness(null);
        }}
      />

      <Modal
        title={
          <span>
            <UserOutlined /> Assign/Change Support Officer for "
            {changingBusinessName}"
          </span>
        }
        open={smSoModalVisible}
        onCancel={() => {
          setSmSoModalVisible(false);
          setSelectedSOId(null);
          setChangingBusinessId(null);
          setChangingBusinessName("");
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setSmSoModalVisible(false);
              setSelectedSOId(null);
              setChangingBusinessId(null);
              setChangingBusinessName("");
            }}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={smSoModalLoading}
            onClick={handleConfirmSOChange}
          >
            Save Changes
          </Button>,
        ]}
        width={500}
      >
        <Spin spinning={smSoModalLoading}>
          <div className="mb-4">
            <p className="text-gray-600">
              Select a Support Officer to assign to this business.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Support Officer
            </label>
            <Select
              placeholder="Select Support Officer"
              value={selectedSOId}
              onChange={(value) => setSelectedSOId(value)}
              allowClear
              options={availableSOs}
              style={{ width: "100%" }}
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>

          {selectedSOId && (
            <div style={{ marginTop: "12px", textAlign: "center" }}>
              <Tag color="blue">
                Support Officer will be assigned to this business
              </Tag>
            </div>
          )}
        </Spin>
      </Modal>

      {/* Filter Modal */}
      <Modal
        title={
          <span>
            <FilterOutlined /> Filters
          </span>
        }
        open={filterModalVisible}
        onCancel={() => setFilterModalVisible(false)}
        footer={[
          <Button
            key="clear"
            onClick={() => {
              setServiceFilter("");
              setStatusFilter("");
              setSeFilter(null);
              setSmFilter(null);
              setSoFilter(null);
              setBusinessStatusFilter(null);
              setZoneFilter(null);
            }}
          >
            Clear All
          </Button>,
          <Button
            key="apply"
            type="primary"
            onClick={() => setFilterModalVisible(false)}
          >
            Apply Filters
          </Button>,
        ]}
        width={500}
      >
        {/* Open/Close Status - All roles */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            Open/Close Status
          </label>
          <Select
            placeholder="Select Status"
            value={statusFilter}
            onChange={setStatusFilter}
            allowClear
            style={{ width: "100%" }}
          >
            {statuses.map((status) => (
              <Option key={status} value={status}>
                {status}
              </Option>
            ))}
          </Select>
        </div>

        {/* Business Status - All roles */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            Business Status
          </label>
          <Select
            placeholder="Select Status"
            value={businessStatusFilter}
            onChange={setBusinessStatusFilter}
            allowClear
            style={{ width: "100%" }}
          >
            {businessStatuses.map((status) => (
              <Option key={status.value} value={status.value}>
                {status.label}
              </Option>
            ))}
          </Select>
        </div>

        {/* Zone - SM, SE and ADMIN */}
        {(userRole === "SM" || userRole === "SE" || userRole === "ADMIN") && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Zone</label>
            <Select
              placeholder="Select Zone"
              value={zoneFilter}
              onChange={setZoneFilter}
              allowClear
              showSearch
              style={{ width: "100%" }}
              options={availableZones}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        )}

        {/* Support Executive - SE and ADMIN */}
        {(userRole === "SE" || userRole === "ADMIN") && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Support Executive
            </label>
            <Select
              placeholder="Select Support Executive"
              value={seFilter}
              onChange={setSeFilter}
              allowClear
              showSearch
              style={{ width: "100%" }}
              options={availableSEs}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        )}

        {/* Support Manager - SE, SM and ADMIN */}
        {(userRole === "SE" || userRole === "SM" || userRole === "ADMIN") && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Support Manager
            </label>
            <Select
              placeholder="Select Support Manager"
              value={smFilter}
              onChange={setSmFilter}
              allowClear
              showSearch
              style={{ width: "100%" }}
              options={availableSMs}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        )}

        {/* Support Officer - SM, SE and ADMIN */}
        {(userRole === "SM" || userRole === "SE" || userRole === "ADMIN") && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Support Officer
            </label>
            <Select
              placeholder="Select Support Officer"
              value={soFilter}
              onChange={setSoFilter}
              allowClear
              showSearch
              style={{ width: "100%" }}
              options={availableSOs}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default BusinessList;
